import { IApplication } from "@rentiohq/shared-frontend/dist/types/application.types";
import { isPetResident } from "utils/application.utils";
import { getResidentsCount, getTotalIncome } from "../DragItem/dragItem.utils";

/**
 * Everything the candidate list and the other-applications dialog read off an
 * application to render its indicators, derived in one place so the two cannot
 * arrive at different numbers for the same record (DEV-3122).
 *
 * Required comes from the row and is never derived here; only delivered is
 * counted, because the document arrays are truthful (DEV-3135).
 */
export const getApplicationIndicators = (application: IApplication) => {
  const contacts = application.contacts ?? [];
  const residents = application.residents ?? [];
  const declaredIncomes = contacts.flatMap(contact => contact.income || []);

  // The date the candidate asked to move in. It is the third thing the match
  // score weighs and had no column, so a row could sit under 100 with nothing
  // marked (DEV-3186).
  //
  // Parsed and checked here rather than at the render, because a value that
  // cannot be read as a date (the MySQL zero date "0000-00-00" is the known
  // one) is truthy, and date-fns throws a RangeError on it instead of printing
  // something harmless. That would take the whole candidate table down while
  // rendering. The scoring path guards the same value in three places for the
  // same reason; this makes the display agree with it, and a date nobody can
  // read is treated as no date at all.
  const parsedMoveInDate = application.dateOfOccupancy
    ? new Date(application.dateOfOccupancy)
    : null;

  return {
    totalIncome: getTotalIncome(contacts),
    moveInDate:
      parsedMoveInDate && !Number.isNaN(parsedMoveInDate.getTime())
        ? parsedMoveInDate
        : null,
    adultCount: getResidentsCount(residents, "INTAKE_RESIDENT_ADULTS"),
    childrenCount: getResidentsCount(residents, "INTAKE_RESIDENT_CHILDREN"),
    petCount: residents.reduce(
      (count, resident) =>
        isPetResident(resident.id) ? count + resident.value : count,
      0,
    ),
    idDocs: {
      required: application.requiredIdDocuments,
      delivered: contacts.filter(
        contact => (contact.documents?.length ?? 0) > 0,
      ).length,
    },
    incomeDocs: {
      required: application.requiredIncomeDocuments,
      delivered: declaredIncomes.filter(
        income => (income.documents?.length ?? 0) > 0,
      ).length,
    },
  };
};
